import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, Pencil } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import { API_PROMOTE_EMPLOYEE } from "../../../../../../config/Endpoints.js";
import {
  Badge,
  Button,
  Modal,
  ResponsiveDataTable,
  Select,
  type ResponsiveColumn,
  type SelectOption,
} from "../../../../ui/index.ts";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import { accessToken } from "../../../../../utils/auth.ts";
import { putApi } from "../../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import type {
  DepartmentRow,
  DesignationRow,
  EmployeeOption,
  EmployeeRow,
} from "../../types.ts";
import { extractApiMessage } from "../../utils.ts";
import styles from "./promotion-management-tab.module.css";

interface PromotionManagementTabProps {
  employees: EmployeeRow[];
  employeeOptions: EmployeeOption[];
  departments: DepartmentRow[];
  loadingEmployees: boolean;
  fetchDesignations: (departmentId: string) => Promise<DesignationRow[]>;
  onPromotionComplete: () => Promise<void> | void;
}

type PromotionTableRow = {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  designation: string;
};

export function PromotionManagementTab({
  employees,
  employeeOptions,
  departments,
  loadingEmployees,
  fetchDesignations,
  onPromotionComplete,
}: PromotionManagementTabProps) {
  const [employeeId, setEmployeeId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [designations, setDesignations] = useState<DesignationRow[]>([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const departmentOptions = useMemo<SelectOption[]>(
    () => departments.map((item) => ({ value: String(item.id), label: item.name })),
    [departments],
  );

  const designationOptions = useMemo<SelectOption[]>(
    () => designations.map((item) => ({ value: String(item.id), label: item.name })),
    [designations],
  );

  const selectedRow = useMemo<PromotionTableRow[]>(() => {
    if (!selectedEmployee) return [];
    return [
      {
        id: String(selectedEmployee.id),
        employeeId: String(selectedEmployee.employeeId ?? selectedEmployee.id),
        employeeName:
          [selectedEmployee.firstName, selectedEmployee.lastName]
            .filter(Boolean)
            .join(" ") ||
          selectedEmployee.empName ||
          "-",
        department: selectedEmployee.department || "-",
        designation: selectedEmployee.designation || "-",
      },
    ];
  }, [selectedEmployee]);

  const loadDesignations = async (
    nextDepartmentId: string,
    employee: EmployeeRow | null,
    preserveCurrentDesignation = true,
  ) => {
    if (!nextDepartmentId) {
      setDesignations([]);
      setDesignationId("");
      return;
    }

    setLoadingDesignations(true);
    try {
      const nextDesignations = await fetchDesignations(nextDepartmentId);
      setDesignations(nextDesignations);
      if (preserveCurrentDesignation) {
        const oldDesignation = nextDesignations.find(
          (item) => item.name === employee?.designation,
        );
        setDesignationId(oldDesignation ? String(oldDesignation.id) : "");
      } else {
        setDesignationId("");
      }
    } catch (e) {
      showError(extractApiMessage(e, "Failed to load designations."));
      setDesignations([]);
      setDesignationId("");
    } finally {
      setLoadingDesignations(false);
    }
  };

  const handleEmployeeChange = (nextEmployeeId: string) => {
    setEmployeeId(nextEmployeeId);
    const employee =
      employees.find((item) => String(item.id) === String(nextEmployeeId)) ?? null;
    setSelectedEmployee(employee);
  };

  const openPromotionModal = () => {
    if (!selectedEmployee) return;
    const oldDepartment = departments.find(
      (item) => item.name === selectedEmployee.department,
    );
    const nextDepartmentId = oldDepartment ? String(oldDepartment.id) : "";
    setDepartmentId(nextDepartmentId);
    setHasChanged(false);
    setModalOpen(true);
    void loadDesignations(nextDepartmentId, selectedEmployee);
  };

  const closePromotionModal = () => {
    setModalOpen(false);
    setDepartmentId("");
    setDesignationId("");
    setDesignations([]);
    setHasChanged(false);
  };

  const handleDepartmentChange = (nextDepartmentId: string) => {
    setDepartmentId(nextDepartmentId);
    setHasChanged(true);
    void loadDesignations(nextDepartmentId, selectedEmployee, false);
  };

  const handleSubmitPromotion = async () => {
    if (!selectedEmployee || !employeeId || !designationId) {
      showError("Please select department and designation.");
      return;
    }

    setSubmitting(true);
    try {
      await putApi(
        `${API_PROMOTE_EMPLOYEE}/${employeeId}/promotions/${designationId}`,
        accessToken(),
        {},
      );
      showSuccess("Successfully promoted.");
      closePromotionModal();
      await onPromotionComplete();
    } catch (e) {
      showError(extractApiMessage(e, "Failed to promote employee."));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!employeeId) {
      setSelectedEmployee(null);
      return;
    }
    const employee = employees.find((item) => String(item.id) === employeeId) ?? null;
    setSelectedEmployee(employee);
  }, [employeeId, employees]);

  const columns = useMemo<ResponsiveColumn<PromotionTableRow>[]>(
    () => [
      {
        id: "employeeId",
        header: "Employee ID",
        accessor: "employeeId",
        sortable: true,
        nowrap: true,
      },
      {
        id: "employeeName",
        header: "Name",
        accessor: "employeeName",
        sortable: true,
      },
      {
        id: "department",
        header: "Department",
        render: (item) =>
          item.department === "-" ? <Badge variant="neutral">Not Set</Badge> : item.department,
      },
      {
        id: "designation",
        header: "Designation",
        render: (item) =>
          item.designation === "-" ? <Badge variant="neutral">Not Set</Badge> : item.designation,
      },
      {
        id: "action",
        header: "Action",
        nowrap: true,
        render: () => (
          <Button
            size="sm"
            startIcon={<Pencil size={14} aria-hidden />}
            onClick={openPromotionModal}
          >
            Promote
          </Button>
        ),
      },
    ],
    [openPromotionModal],
  );

  return (
    <div className={styles.stack}>
      <section className={styles.panel}>
      
        <div className={styles.employeePicker}>
          <Select
            id="employee-progression-promotion-employee"
            label="Employee"
            value={employeeId}
            onChange={handleEmployeeChange}
            options={employeeOptions}
            placeholder={loadingEmployees ? "Loading..." : "Select Employee Name"}
            searchable
            disabled={loadingEmployees || employeeOptions.length === 0}
          />
        </div>
      </section>

      <section className={styles.panel}>
      
        <ResponsiveDataTable
          columns={columns}
          data={selectedRow}
          aria-label="Promotion Management"
          emptyState={
            <EmptyStateMessage message="Select an employee to manage promotion details." />
          }
        />
      </section>

      <Modal
        open={modalOpen}
        onClose={closePromotionModal}
        title="Promote Employee"
        size="lg"
        footer={
          <>
          <div className={styles.modalFooter}>

              <Button variant="light-grey" onClick={closePromotionModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPromotion}
              disabled={submitting || !hasChanged || !designationId}
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
            </div>
          </>
        }
      >
        <div className={styles.modalGrid}>
          <Select
            id="employee-progression-promotion-department"
            label="Department"
            value={departmentId}
            onChange={handleDepartmentChange}
            options={departmentOptions}
            placeholder="Select Department"
            searchable
            required
          />
          <Select
            id="employee-progression-promotion-designation"
            label="Designation"
            
            value={designationId}
            onChange={(next) => {
              setDesignationId(next);
              setHasChanged(true);
            }}
            options={designationOptions}
            placeholder="Select Designation"
            searchable
            required
            disabled={!departmentId || loadingDesignations}
          />
        </div>
        {loadingDesignations ? (
          <div className={styles.loadingInline}>
            <ThreeDots height="32" width="48" color="var(--color-accent-500)" visible />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

